'use client'

import { useState } from 'react'
import type { Route } from 'next'
import { useRouter } from 'next/navigation'
import { ArrowLeft, Clock, Eye, TrendingUp } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Progress } from '@/components/ui/progress'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import {
  CASE_STATUS_PIPELINE,
  CaseAnalysis,
  CaseGoals,
  CaseStatusBadge,
  SERVICE_TYPE_TO_FLOW,
  toMockCaseRecord,
  toMockHoldings,
  toMockSteps,
  toMockTrades,
  useCaseGoals,
  useCaseHoldings,
  useCasePrescriptionSteps,
  useCaseTrades,
  usePreviewCaseForFlow,
  type CaseRecord,
  type FlowType,
} from '@/features/cases'
import { useInvestorCasesForStaff, useInvestorDetail } from '@/features/investors'
import { useMe } from '@/features/session'
import { AuditHero } from '@/features/investor/components/home/audit-hero'
import { TradesHero } from '@/features/investor/components/home/trades-hero'

const FLOW_TAB_LABEL: Record<FlowType, string> = {
  audit: 'Portfolio Audit',
  trades: 'Trades Audit',
  build: 'Build My Portfolio',
}

const FLOW_SECTION_COPY: Record<'audit' | 'trades', { title: string; description: string }> = {
  audit: {
    title: 'Holdings',
    description: 'Every scheme in the CAS, with allocation and gain/loss.',
  },
  trades: {
    title: 'Trades',
    description: 'Every trade, read straight from the tradebook.',
  },
}

/** No case at all for this flow yet — a plain, non-actionable placeholder. Deliberately not the
 * real `NotRequestedCard` (its CTA routes to the investor-only "request a service" flow, which
 * makes no sense from a staff session). */
function PreviewNotRequestedCard({ flow }: { flow: FlowType }) {
  return (
    <Card>
      <CardContent className="flex flex-col items-center gap-2 py-10 text-center">
        <p className="text-base font-semibold">{FLOW_TAB_LABEL[flow]}</p>
        <p className="text-muted-foreground max-w-sm text-sm">
          This investor hasn&apos;t requested this service yet — nothing to preview here.
        </p>
      </CardContent>
    </Card>
  )
}

/** Case exists for this flow but isn't published yet — same content as the real investor-facing
 * `PendingCaseCard`, minus its "Track status" button. That button `router.push`es straight to
 * `/review-status`, a route server-guarded to investors only — from a staff/admin preview session
 * it would redirect out of the preview entirely instead of tracking anything. Not reused from
 * `PendingCaseCard` directly for the same reason `PreviewNotRequestedCard` isn't: the real card's
 * CTA makes no sense outside an investor's own session. */
function PreviewPendingCard({ caseRecord }: { caseRecord: Pick<CaseRecord, 'flow' | 'status'> }) {
  const stepIndex = CASE_STATUS_PIPELINE.indexOf(caseRecord.status)
  const totalSteps = CASE_STATUS_PIPELINE.length
  const progressPct = ((stepIndex + 1) / totalSteps) * 100

  return (
    <Card>
      <CardContent className="flex flex-col gap-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex min-w-0 items-start gap-3">
            <span className="bg-primary/10 text-primary flex size-11 shrink-0 items-center justify-center rounded-full">
              <Clock className="size-5" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-base font-semibold">{FLOW_TAB_LABEL[caseRecord.flow]}</p>
              <p className="text-muted-foreground mt-1 text-sm">
                Still with your advisor — this unlocks once it&apos;s published.
              </p>
            </div>
          </div>
          {/* ml-14 matches the progress row's own pl-14 below (icon's size-11 + its gap-3) — so if
              this row wraps on a narrow screen, the badge lines up under the title column instead
              of sitting flush against the card's left edge, under the icon. */}
          <CaseStatusBadge status={caseRecord.status} className="ml-14" />
        </div>

        <div className="flex flex-wrap items-center gap-3 pl-14">
          <div className="flex min-w-0 flex-1 items-center gap-3">
            <Progress value={progressPct} className="h-1.5" />
            <span className="text-muted-foreground shrink-0 text-xs font-medium">
              Step {stepIndex + 1} of {totalSteps}
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

/**
 * Staff- and admin-facing (both read-only). Renders exactly what the investor's own dashboard Home page shows — same
 * branching (not-requested / pending / published-shaped content) and the same presentational
 * components (`AuditHero`/`TradesHero`/`CaseAnalysis`) as `InvestorDashboardView` — but resolves
 * "which case" from an explicit `investorId` via `usePreviewCaseForFlow` instead of the signed-in
 * session's own identity. Read-only: no edit/mutation affordances of any kind, and no real investor
 * route/component/guard is touched by this feature.
 */
export function ClientDashboardPreview({
  investorId,
  defaultFlow,
  mode,
}: {
  investorId: string
  defaultFlow: FlowType
  mode: 'staff' | 'admin'
}) {
  const router = useRouter()
  const pathPrefix = mode === 'admin' ? '/admin/clients' : '/staff/clients'
  const isStaff = mode === 'staff'
  const { data: investor } = useInvestorDetail(investorId)
  const meQuery = useMe()
  const investorCasesQuery = useInvestorCasesForStaff(investorId)
  const [tab, setTab] = useState<FlowType>(defaultFlow)

  const query = usePreviewCaseForFlow(investorId, tab)

  // Same "predict the backend's 404 client-side" reasoning as `client-detail.tsx`'s
  // `canOpenCase`/`caseOwnerLabel` (`getPreview` calls the identical `assertCaseAccess`, so opening
  // a colleague's case here would 404 just like the real Client Detail screen) — this component was
  // missing the equivalent tab-disabling entirely. Admin has no such restriction (`!isStaff` short-
  // circuits to `true`), matching the real screen. A flow with no case yet has nothing to "own," so
  // it stays enabled — `PreviewNotRequestedCard` already handles that state. An UNassigned sibling
  // case is openable too, same exception `canOpenCase` carries — staff can read (not edit) an
  // unclaimed case to decide whether to claim it, so its tab shouldn't be stuck disabled here either.
  const latestCaseForFlow = (flow: FlowType) =>
    investorCasesQuery.data
      ?.filter((c) => SERVICE_TYPE_TO_FLOW[c.serviceType] === flow)
      .sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1))[0]
  const canOpenFlow = (flow: FlowType) => {
    const c = latestCaseForFlow(flow)
    return !isStaff || !c || c.advisorId === meQuery.data?.id || c.advisorId === null
  }
  const flowOwnerLabel = (flow: FlowType) => {
    const c = latestCaseForFlow(flow)
    return c?.advisorName
      ? `Handled by ${c.advisorName}${c.advisorEmail ? ` (${c.advisorEmail})` : ''}`
      : 'Unassigned'
  }
  const holdingsQuery = useCaseHoldings(query.data?.id)
  const tradesQuery = useCaseTrades(query.data?.id, 1, 50)
  const stepsQuery = useCasePrescriptionSteps(query.data?.id)
  const goalsQuery = useCaseGoals(query.data?.id)

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Button variant="ghost" size="sm" className="-ml-2 w-fit" onClick={() => router.back()}>
          <ArrowLeft className="size-4" /> Back to case
        </Button>
      </div>

      <div className="border-primary/20 bg-primary/5 flex items-center gap-2 rounded-lg border px-3 py-2">
        <Eye className="text-primary size-4 shrink-0" />
        <p className="text-xs leading-snug">
          <span className="font-semibold">Investor View</span> — this is exactly what{' '}
          {investor?.name ?? 'this investor'} will see once this service is published. Not editable
          from here.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Tabs value={tab} onValueChange={(v) => setTab(v as FlowType)}>
          <TabsList>
            {(Object.keys(FLOW_TAB_LABEL) as FlowType[]).map((flow) =>
              canOpenFlow(flow) ? (
                <TabsTrigger key={flow} value={flow}>
                  {FLOW_TAB_LABEL[flow]}
                </TabsTrigger>
              ) : (
                // Disabled, not a normal tab — same span-wrapper pattern as `client-detail.tsx`
                // (a disabled element doesn't reliably fire hover events).
                <Tooltip key={flow}>
                  <TooltipTrigger asChild>
                    <span className="inline-flex">
                      <TabsTrigger value={flow} disabled>
                        {FLOW_TAB_LABEL[flow]}
                      </TabsTrigger>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>{flowOwnerLabel(flow)}</TooltipContent>
                </Tooltip>
              ),
            )}
          </TabsList>
        </Tabs>
      </div>

      {query.isListLoading ? (
        <Skeleton className="h-40 w-full" />
      ) : !query.summary ? (
        <PreviewNotRequestedCard flow={tab} />
      ) : query.isLoading || !query.data ? (
        <Skeleton className="h-40 w-full" />
      ) : query.data.caseStatus !== 'published' ? (
        <PreviewPendingCard caseRecord={toMockCaseRecord(query.data)} />
      ) : tab === 'build' ? (
        <CaseGoals
          caseId={query.data.id}
          goals={goalsQuery.data ?? []}
          buildProfile={query.data.buildProfile}
          investorAge={query.data.investor?.age ?? null}
        />
      ) : (
        <div className="flex flex-col gap-4">
          {tab === 'audit' && (
            <AuditHero
              caseRecord={toMockCaseRecord(query.data)}
              injectedHoldings={
                holdingsQuery.data ? toMockHoldings(holdingsQuery.data, query.data.id) : []
              }
              injectedSteps={stepsQuery.data ? toMockSteps(stepsQuery.data) : []}
              onGrowthPlanStepsClick={() => {
                // Widened to `string` — see the identical note on client-detail.tsx's Preview
                // button — otherwise the literal template collapses into a route-typed pattern
                // that Next's `typedRoutes` then rejects for carrying a query string.
                const flow: string = tab
                // Cast: see the identical note on client-detail.tsx's preview-button `router.push`.
                router.push(
                  `${pathPrefix}/${investorId}/preview/prescription?flow=${flow}` as Route,
                )
              }}
            />
          )}
          {tab === 'trades' && (
            <TradesHero
              caseRecord={toMockCaseRecord(query.data)}
              injectedTrades={
                tradesQuery.data ? toMockTrades(tradesQuery.data.items, query.data.id) : []
              }
            />
          )}
          <Card>
            <CardHeader className="flex flex-row items-start gap-3 space-y-0">
              <span className="bg-primary/10 text-primary flex size-10 shrink-0 items-center justify-center rounded-lg">
                <TrendingUp className="size-5" />
              </span>
              <div>
                <CardTitle className="text-base font-medium">
                  {FLOW_SECTION_COPY[tab].title}
                </CardTitle>
                <CardDescription>{FLOW_SECTION_COPY[tab].description}</CardDescription>
              </div>
            </CardHeader>
            <CardContent>
              <CaseAnalysis
                caseRecord={toMockCaseRecord(query.data)}
                hideSummary
                injectedHoldings={
                  holdingsQuery.data ? toMockHoldings(holdingsQuery.data, query.data.id) : []
                }
                injectedTrades={
                  tradesQuery.data ? toMockTrades(tradesQuery.data.items, query.data.id) : []
                }
              />
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  )
}
